import { ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon } from "@/components/icons"
import Dropdown from "@/components/ui/dropdown"
import Input from "@/components/ui/input"
import { renderPaginationNumbers } from "@/utils/pagination"
import TableCell from "./TableCell"

import "./index.scss"

import { TableProps } from "@/types/component"
import { t } from "i18next"
import React, { useState, useEffect } from "react"

const Table = <T,>({
  columns,
  currentPage = 1,
  data,
  emptyMessage = t("common.noMatchingData"),
  filterDropdowns,
  getRowId,
  isLoading = false,
  onPageChange,
  onPageSizeChange,
  onRowSelect,
  onRowClick,
  onSearch,
  onSelectAll,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  renderActions,
  actionsColumnHeader,
  searchable = false,
  selectable = false,
  isRowSelectable,
  selectedRows = [],
  totalRecords,
  openDropdowns = {},
  hasVisibleActions = true,
  contentBasedHeight = false,
}: TableProps<T> & { 
  openDropdowns?: { [key: string]: boolean };
  hasVisibleActions?: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPageSize, setCurrentPageSize] = useState(pageSize.toString())
  const [internalLoading, setInternalLoading] = useState(false)

  useEffect(() => {
    if (isLoading) {
      setInternalLoading(true)
    } else {
      const timer = setTimeout(() => {
        setInternalLoading(false)
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [isLoading])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    if (onSearch) {
      onSearch(value)
    }
  }

  const totalPages = totalRecords && pageSize ? Math.ceil(totalRecords / pageSize) : 1

  const isRowSelected = (row: T) => {
    if (!getRowId) return false
    const rowId = getRowId(row)
    return selectedRows.some((selectedRow) => getRowId(selectedRow) === rowId)
  }

  const selectableRows = isRowSelectable ? data.filter(isRowSelectable) : data
  const isAllSelected = () =>
    selectableRows.length > 0 &&
    selectableRows.every((row) => isRowSelected(row))

  const handleRowSelect = (row: T) => {
    if (onRowSelect && (!isRowSelectable || isRowSelectable(row))) {
      onRowSelect(row)
    }
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelectAll) {
      onSelectAll(e.target.checked)
    }
  }

  const handlePageSizeChange = (value: string) => {
    setCurrentPageSize(value)
    if (onPageSizeChange) {
      onPageSizeChange(Number(value))
    }
  }

  const renderTableSkeleton = () => {
    const skeletonRows = Array(pageSize || 10).fill(null)

    return (
      <div className="table-wrapper">
        <table className="table">
          <thead className="table-header">
            <tr>
              {columns.map((col, index) => (
                <th className={col.className} key={index}>
                  <div className="table-skeleton__header-cell">
                    {selectable && index === 0 && (
                      <div className="table-skeleton__checkbox"></div>
                    )}
                    <div className="table-skeleton__header-text"></div>
                  </div>
                </th>
              ))}
              {renderActions && (
                <th>
                  <div className="table-skeleton__header-text table-skeleton__header-text--short"></div>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="table-body table-skeleton__body">
            {skeletonRows.map((_, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col, colIndex) => (
                  <td className={col.className} key={colIndex}>
                    <div className="table-skeleton__cell-wrapper">
                      {selectable && colIndex === 0 && (
                        <div className="table-skeleton__checkbox"></div>
                      )}
                      <div
                        className={`table-skeleton__cell ${colIndex % 3 === 0 ? 'table-skeleton__cell--short' :
                          colIndex % 3 === 1 ? 'table-skeleton__cell--medium' :
                            'table-skeleton__cell--long'
                          }`}
                      ></div>
                    </div>
                  </td>
                ))}
                {renderActions && (
                  <td>
                    <div className="table-skeleton__actions">
                      <div className="table-skeleton__action-dot"></div>
                      <div className="table-skeleton__action-dot"></div>
                      <div className="table-skeleton__action-dot"></div>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Determine if pagination will be rendered (same condition as pagination render)
  const willShowPagination = !!(totalRecords && pageSize && onPageChange)
  const shouldExpandRows = willShowPagination && data.length === pageSize
  
  // Four modes:
  // 1. Content-based height (explicit or no pagination): content-based height
  // 2. Pagination + full page data: fill remaining space, body scrolls internally
  // 3. Pagination + partial data: fixed row height with scroll
  // 4. No pagination (modal/embedded tables): content-based height
  const tableBodyClass = contentBasedHeight || !willShowPagination
    ? "flex-table-body flex-table-body--content" 
    : shouldExpandRows 
      ? "flex-table-body flex-table-body--expand" 
      : "flex-table-body"

  return (
    <div className="table-container">
      {filterDropdowns && filterDropdowns.length >= 1 && (
        <div className="table-filters">
          <div className="filter">
            {filterDropdowns?.map((filter, index) => (
              <Dropdown
                allowClear={true}
                key={index}
                label={filter.label}
                onSelect={(value) => filter.onSelect(Array.isArray(value) ? value[0] : value)}
                options={filter.options.map((option) => ({
                  label: option,
                  value: option,
                }))}
                placeholder={filter.placeholder}
                value={null}
              />
            ))}
          </div>
          <div className="search-input">
            {searchable && (
              <Input
                onChange={handleSearchChange}
                placeholder="Search..."
                type="text"
                value={searchTerm}
              />
            )}
          </div>
        </div>
      )}
      {internalLoading ? (
        renderTableSkeleton()
      ) : (
        <>
          <div className="table-wrapper">
            <div className="flex-table">
              {/* Header */}
              <div className="flex-table-header">
                {columns.map((col, index) => (
                  <div className={`flex-cell ${col.className || ''}`} key={index} style={col.style}>
                    {selectable && index === 0 && (
                      <input 
                        checked={isAllSelected()} 
                        onChange={handleSelectAll} 
                        type="checkbox" 
                        className="row-checkbox"
                      />
                    )}
                    {col.header}
                  </div>
                ))}
                {renderActions && hasVisibleActions && <div className="flex-cell actions-column">{actionsColumnHeader ?? t("common.actions")}</div>}
              </div>

              {/* Body */}
              <div className={tableBodyClass}>
                {data && data?.length > 0 ? (
                  data.map((row, rowIndex) => {
                    const rowId = getRowId ? getRowId(row) : rowIndex.toString()
                    const hasOpenDropdown = openDropdowns[rowId] || false
                    
                    return (
                      <div 
                        key={rowId} 
                        className={`flex-table-row ${hasOpenDropdown ? 'has-open-dropdown' : ''}`}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                      >
                        {columns.map((col, colIndex) => {
                          const cellContent =
                            typeof col.accessor === "function"
                              ? col.accessor(row)
                              : (row[col.accessor as keyof T] as React.ReactNode)

                          return (
                            <TableCell
                              key={colIndex}
                              cellContent={cellContent}
                              className={col.className}
                              style={col.style}
                              colIndex={colIndex}
                              selectable={selectable}
                              isFirstColumn={colIndex === 0}
                              isRowSelected={isRowSelected(row)}
                              isRowSelectDisabled={isRowSelectable ? !isRowSelectable(row) : false}
                              onRowSelect={() => handleRowSelect(row)}
                            />
                          )
                        })}
                        {renderActions && hasVisibleActions && (
                          <div className="flex-cell actions-column">
                            {renderActions(row, rowIndex)}
                          </div>
                        )}
                      </div>
                    )
                  })
                ) : (
                  <div className="flex-table-empty">
                    <div className="flex-cell">
                      {emptyMessage}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {totalRecords && pageSize && onPageChange ? (
            <div className="table-pagination">
              <div className="pagination-left">
                <span className="pagination-label">{t("pagination.show")}</span>
                <Dropdown
                  onSelect={(value) => handlePageSizeChange(Array.isArray(value) ? value[0] : value)}
                  options={pageSizeOptions.map((size) => ({
                    label: size.toString(),
                    value: size.toString(),
                  }))}
                  menuPosition="top"
                  selectedLabel={currentPageSize}
                  value={currentPageSize}
                />
                <span className="pagination-details">
                  {t("pagination.details", {
                    total: totalRecords,
                    start: currentPage * pageSize - pageSize + 1,
                    end: Math.min(currentPage * pageSize, totalRecords),
                  })}
                </span>
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-arrow"
                  disabled={currentPage === 1}
                  onClick={() => onPageChange(currentPage - 1)}
                >
                  <ArrowLeftIcon height={16} width={16} />
                </button>
                {renderPaginationNumbers(currentPage, totalPages, onPageChange)}
                <button
                  className="pagination-arrow"
                  disabled={currentPage === totalPages}
                  onClick={() => onPageChange(currentPage + 1)}
                >
                  <ArrowRightIcon height={16} width={16} />
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default Table
